import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { getCurrentPracticeId, getCurrentUserInfo } from "../lib/api";
import { db } from "../lib/db";
import { storage } from "../lib/storage";
import { logAudit } from "../utils/auditLog";
import { useApp } from "../context/AppContext";
import Pagination from "./Pagination";
import { compressImage } from "../utils/imageCompressor";
import DICOMViewer from "./DICOMViewer";
import { todayISO } from "../utils/chartHelpers";
const PAGE_SIZE = 25;
const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  navy:   "#0f1e2e", navy2: "#162436", navy3: "#1d2f40", navy4: "#243a4e",
  teal:   "#0ea5a0", tealLt: "rgba(14,165,160,0.12)",
  amber:  "#f0a500", amberLt: "rgba(240,165,0,0.12)",
  red:    "#e05252", redLt: "rgba(224,82,82,0.12)",
  green:  "#22c55e", greenLt: "rgba(34,197,94,0.12)",
  purple: "#8b5cf6", purpleLt: "rgba(139,92,246,0.12)",
  blue:   "#3b82f6", blueLt: "rgba(59,130,246,0.12)",
  text1:  "#e8edf2", text2: "#8fa3b4", text3: "#546e7a",
  border: "rgba(255,255,255,0.07)", border2: "rgba(255,255,255,0.12)",
};

// ─── STUDY TYPE CONFIG ────────────────────────────────────────────────────────
const STUDY_CONFIG = {
  "X-ray":       { icon: "◻", color: C.teal,   bg: C.tealLt   },
  "MRI":         { icon: "⊛", color: C.purple, bg: C.purpleLt },
  "CT":          { icon: "⊕", color: C.amber,  bg: C.amberLt  },
  "Ultrasound":  { icon: "◉", color: C.blue,   bg: C.blueLt   },
  "Bone Scan":   { icon: "⬡", color: C.green,  bg: C.greenLt  },
  "DEXA":        { icon: "◑", color: "#f97316", bg: "rgba(249,115,22,0.12)" },
  "Fluoroscopy": { icon: "◫", color: C.red,    bg: C.redLt    },
};

const STUDY_TYPES  = Object.keys(STUDY_CONFIG);
const BODY_PARTS   = ["Hand","Wrist","Forearm","Elbow","Humerus","Shoulder","Clavicle","Cervical Spine","Lumbar Spine","Hip","Pelvis","Knee","Ankle","Foot","Whole Body"];
const FACILITIES   = ["In-house","Radiology Partners","SimonMed","Banner Imaging","Outside Facility"];
const PROVIDERS    = ["Dr. Walker","Dr. Patel","Dr. Chen","Dr. Moss"];
const LATERALITIES = ["Right","Left","Bilateral","N/A"];
const PRIORITIES   = ["Routine","STAT"];

// ─── MOCK DATA ────────────────────────────────────────────────────────────────
const dAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split("T")[0]; };
const TODAY = todayISO();

export const MOCK_IMAGING = [
  {
    id:"IMG001", patient_id:"P001", patient_name:"James Hartley",
    study_type:"MRI", body_part:"Shoulder", laterality:"Right",
    ordered_by:"Dr. Walker", order_date:dAgo(45), study_date:dAgo(42),
    facility:"Radiology Partners", priority:"routine", status:"resulted",
    report_text:`CLINICAL INDICATION: Right shoulder pain, suspected rotator cuff tear. Patient reports 3 months of progressive pain and weakness with overhead activity.

TECHNIQUE: MRI right shoulder without contrast. Proton density fat-saturated coronal, sagittal, and axial sequences obtained at 3T.

FINDINGS: The supraspinatus tendon demonstrates a full-thickness tear measuring approximately 2.1 cm in the anteroposterior dimension with 1.4 cm of medial retraction. The musculotendinous junction is intact. The infraspinatus tendon demonstrates mild tendinopathy without discrete tear. The subscapularis tendon is intact. There is mild to moderate fluid in the subacromial-subdeltoid bursa consistent with bursitis. The long head of the biceps tendon is intact within the bicipital groove without subluxation. The glenohumeral labrum is intact. The AC joint demonstrates mild degenerative changes with small inferior osteophyte formation.

IMPRESSION:
1. Full-thickness supraspinatus tendon tear measuring 2.1 cm AP with 1.4 cm of medial retraction.
2. Mild infraspinatus tendinopathy, no discrete tear.
3. Mild to moderate subacromial-subdeltoid bursitis.
4. Intact subscapularis tendon and glenohumeral labrum.
5. Mild AC joint degenerative changes.`,
    impression:"1. Full-thickness supraspinatus tendon tear measuring 2.1 cm AP with 1.4 cm of medial retraction.\n2. Mild infraspinatus tendinopathy, no discrete tear.\n3. Mild to moderate subacromial-subdeltoid bursitis.",
    image_urls:[], is_outside_study:false, notes:"",
  },
  {
    id:"IMG002", patient_id:"P001", patient_name:"James Hartley",
    study_type:"X-ray", body_part:"Shoulder", laterality:"Right",
    ordered_by:"Dr. Walker", order_date:dAgo(60), study_date:dAgo(59),
    facility:"In-house", priority:"routine", status:"resulted",
    report_text:`CLINICAL INDICATION: Right shoulder pain, evaluate for fracture or significant osseous pathology.

TECHNIQUE: AP, scapular Y, and axillary radiographs of the right shoulder.

FINDINGS: The glenohumeral joint is maintained without dislocation. The humeral head demonstrates mild superior migration suggesting rotator cuff pathology. There is mild subacromial space narrowing measuring approximately 7 mm (normal >10 mm). Subchondral sclerosis is noted along the greater tuberosity. No acute fracture or dislocation. The AC joint demonstrates Grade I degenerative changes. No significant calcific tendinitis deposits.

IMPRESSION:
1. Mild superior humeral head migration with reduced subacromial space, suggesting rotator cuff insufficiency. Clinical correlation recommended.
2. Subchondral sclerosis of the greater tuberosity consistent with chronic rotator cuff pathology.
3. Mild AC joint degenerative changes.
4. No acute fracture or dislocation.`,
    impression:"1. Mild superior humeral head migration with reduced subacromial space, suggesting rotator cuff insufficiency.\n2. Subchondral sclerosis of the greater tuberosity consistent with chronic rotator cuff pathology.\n3. No acute fracture or dislocation.",
    image_urls:[], is_outside_study:false, notes:"",
  },
  {
    id:"IMG003", patient_id:"P001", patient_name:"James Hartley",
    study_type:"CT", body_part:"Shoulder", laterality:"Right",
    ordered_by:"Dr. Walker", order_date:dAgo(10), study_date:null,
    facility:"SimonMed", priority:"routine", status:"scheduled",
    report_text:null, impression:null, image_urls:[], is_outside_study:false,
    notes:"Pre-operative CT arthrogram ordered for surgical planning.",
  },
  {
    id:"IMG004", patient_id:"P002", patient_name:"Maria Delgado",
    study_type:"X-ray", body_part:"Wrist", laterality:"Right",
    ordered_by:"Dr. Walker", order_date:dAgo(68), study_date:dAgo(68),
    facility:"In-house", priority:"STAT", status:"resulted",
    report_text:`CLINICAL INDICATION: Right wrist pain and swelling following mechanical fall onto outstretched hand. Evaluate for fracture.

TECHNIQUE: PA, lateral, and oblique radiographs of the right wrist.

FINDINGS: There is a comminuted intra-articular fracture of the distal radius with approximately 5 mm of dorsal angulation and 3 mm of radial shortening. The fracture line extends into the radiocarpal joint. The ulnar styloid is intact. No perilunate or lunate dislocation. The carpal bones are intact without scaphoid fracture. No significant soft tissue swelling noted.

IMPRESSION:
1. Comminuted intra-articular distal radius fracture with dorsal angulation (5 mm) and radial shortening (3 mm). Orthopedic consultation recommended.
2. Intact ulnar styloid and carpal bones.
3. No carpal instability pattern identified.`,
    impression:"1. Comminuted intra-articular distal radius fracture with dorsal angulation (5 mm) and radial shortening (3 mm). Orthopedic consultation recommended.\n2. Intact ulnar styloid and carpal bones.",
    image_urls:[], is_outside_study:false, notes:"STAT study — fracture confirmed in ED.",
  },
  {
    id:"IMG005", patient_id:"P002", patient_name:"Maria Delgado",
    study_type:"X-ray", body_part:"Wrist", laterality:"Right",
    ordered_by:"Dr. Walker", order_date:dAgo(40), study_date:dAgo(40),
    facility:"In-house", priority:"routine", status:"resulted",
    report_text:`CLINICAL INDICATION: Post-reduction right distal radius fracture, assess alignment.

TECHNIQUE: PA and lateral radiographs of the right wrist with cast.

FINDINGS: Cast is present. The previously identified distal radius fracture demonstrates improved alignment with current dorsal angulation of approximately 2 mm. Radial length is maintained within 1 mm. No evidence of hardware failure. The fracture line is partially visible with early periosteal bridging callus formation suggesting early healing. No cast complications identified.

IMPRESSION:
1. Distal radius fracture with improved alignment following reduction.
2. Early periosteal callus formation indicating healing.
3. No cast complications or hardware issues.`,
    impression:"1. Distal radius fracture with improved alignment following reduction.\n2. Early periosteal callus formation indicating healing.",
    image_urls:[], is_outside_study:false, notes:"6-week post-reduction follow-up film.",
  },
  {
    id:"IMG006", patient_id:"P002", patient_name:"Maria Delgado",
    study_type:"MRI", body_part:"Wrist", laterality:"Right",
    ordered_by:"Dr. Walker", order_date:dAgo(20), study_date:dAgo(18),
    facility:"Radiology Partners", priority:"routine", status:"resulted",
    report_text:`CLINICAL INDICATION: Right wrist pain with suspected carpal tunnel syndrome and TFCC pathology following healed distal radius fracture.

TECHNIQUE: MRI right wrist without contrast, 3T, using dedicated wrist coil.

FINDINGS: The carpal tunnel is evaluated. The median nerve demonstrates increased signal and mild enlargement proximal to the carpal tunnel consistent with carpal tunnel syndrome. The flexor tendons within the carpal tunnel are unremarkable. The TFCC demonstrates mild central disc degeneration without discrete perforation. The scapholunate and lunotriquetral ligaments appear intact. The healed distal radius fracture site shows mature sclerosis with no residual deformity. The extrinsic wrist ligaments are intact.

IMPRESSION:
1. Median nerve enlargement and signal alteration consistent with carpal tunnel syndrome, moderate severity.
2. Mild TFCC central disc degeneration without discrete perforation.
3. Healed distal radius fracture with mature sclerosis.
4. Intact intrinsic and extrinsic wrist ligaments.`,
    impression:"1. Median nerve enlargement and signal alteration consistent with carpal tunnel syndrome, moderate severity.\n2. Mild TFCC central disc degeneration without discrete perforation.\n3. Healed distal radius fracture with mature sclerosis.",
    image_urls:[], is_outside_study:false, notes:"",
  },
  {
    id:"IMG007", patient_id:"P003", patient_name:"Robert Chen",
    study_type:"X-ray", body_part:"Hand", laterality:"Right",
    ordered_by:"Dr. Walker", order_date:dAgo(90), study_date:dAgo(89),
    facility:"In-house", priority:"routine", status:"resulted",
    report_text:`CLINICAL INDICATION: Right ring finger pain and triggering. Evaluate for osseous pathology.

TECHNIQUE: PA, lateral, and oblique radiographs of the right hand.

FINDINGS: No acute fracture or dislocation. The metacarpal and phalangeal cortices are intact. There is mild joint space narrowing of the right ring PIP joint. Minimal periarticular soft tissue swelling is identified around the ring finger. No significant degenerative changes of the CMC joints. Bone density appears normal.

IMPRESSION:
1. Mild right ring PIP joint space narrowing, likely early degenerative change.
2. No acute fracture or dislocation.
3. Soft tissue findings are non-specific; clinical correlation with trigger finger diagnosis recommended.`,
    impression:"1. Mild right ring PIP joint space narrowing, likely early degenerative change.\n2. No acute fracture or dislocation.\n3. Clinical correlation with trigger finger diagnosis recommended.",
    image_urls:[], is_outside_study:false, notes:"",
  },
  {
    id:"IMG008", patient_id:"P003", patient_name:"Robert Chen",
    study_type:"MRI", body_part:"Hand", laterality:"Right",
    ordered_by:"Dr. Walker", order_date:dAgo(5), study_date:null,
    facility:"Radiology Partners", priority:"STAT", status:"ordered",
    report_text:null, impression:null, image_urls:[], is_outside_study:false,
    notes:"STAT MRI ordered to evaluate for A1 pulley tear and tenosynovitis.",
  },
  {
    id:"IMG009", patient_id:"P004", patient_name:"Patricia Monroe",
    study_type:"X-ray", body_part:"Hand", laterality:"Right",
    ordered_by:"Dr. Walker", order_date:dAgo(120), study_date:dAgo(120),
    facility:"In-house", priority:"routine", status:"resulted",
    report_text:`CLINICAL INDICATION: Right thumb pain at base, CMC joint arthritis evaluation.

TECHNIQUE: PA, lateral, and stress radiographs of the right thumb.

FINDINGS: There is moderate joint space narrowing of the right trapeziometacarpal (CMC) joint with subchondral sclerosis and marginal osteophyte formation consistent with Grade II-III carpometacarpal arthritis. The scaphoid-trapezium-trapezoid (STT) joint demonstrates mild degenerative changes. No acute fracture. Cortical density appears mildly reduced, correlate with BMD if not recently obtained.

IMPRESSION:
1. Moderate right thumb CMC joint osteoarthritis, Grade II-III.
2. Mild STT joint degenerative changes.
3. Mildly reduced cortical density — recommend DEXA scan if not recently performed.`,
    impression:"1. Moderate right thumb CMC joint osteoarthritis, Grade II-III.\n2. Mild STT joint degenerative changes.\n3. Recommend DEXA scan if not recently performed.",
    image_urls:[], is_outside_study:false, notes:"",
  },
  {
    id:"IMG010", patient_id:"P004", patient_name:"Patricia Monroe",
    study_type:"DEXA", body_part:"Whole Body", laterality:"N/A",
    ordered_by:"Dr. Walker", order_date:dAgo(100), study_date:dAgo(95),
    facility:"Banner Imaging", priority:"routine", status:"resulted",
    report_text:`CLINICAL INDICATION: Osteoporosis screening. Patient is a 67-year-old female with suspected metabolic bone disease.

TECHNIQUE: Dual-energy X-ray absorptiometry (DEXA) of the lumbar spine and bilateral proximal femurs per standard protocol.

FINDINGS:
Lumbar Spine (L1-L4): BMD 0.741 g/cm², T-score -2.9
Right Femoral Neck: BMD 0.624 g/cm², T-score -2.6
Left Femoral Neck: BMD 0.638 g/cm², T-score -2.5
Total Hip Right: BMD 0.718 g/cm², T-score -2.3

IMPRESSION:
1. Lumbar spine T-score -2.9, consistent with osteoporosis (WHO criteria: T-score ≤ -2.5).
2. Bilateral femoral neck T-scores consistent with osteoporosis.
3. Pharmacologic therapy and fall prevention counseling recommended.
4. Baseline scan — recommend repeat in 1-2 years to assess treatment response.`,
    impression:"1. Lumbar spine T-score -2.9, consistent with osteoporosis (WHO criteria).\n2. Bilateral femoral neck T-scores consistent with osteoporosis.\n3. Pharmacologic therapy and fall prevention counseling recommended.",
    image_urls:[], is_outside_study:false, notes:"Referred to endocrinology for bisphosphonate management.",
  },
  {
    id:"IMG011", patient_id:"P004", patient_name:"Patricia Monroe",
    study_type:"X-ray", body_part:"Knee", laterality:"Right",
    ordered_by:"Dr. Walker", order_date:dAgo(50), study_date:dAgo(50),
    facility:"In-house", priority:"routine", status:"resulted",
    report_text:`CLINICAL INDICATION: Right knee pain. Evaluate for arthritis.

TECHNIQUE: Standing AP, lateral, and Merchant view radiographs of the right knee.

FINDINGS: There is moderate medial compartment joint space narrowing with subchondral sclerosis and marginal osteophytes. The lateral compartment is relatively preserved. Mild patellofemoral joint space narrowing is noted. No acute fracture or significant effusion on plain film.

IMPRESSION:
1. Moderate medial compartment osteoarthritis, Kellgren-Lawrence Grade III.
2. Mild patellofemoral degenerative changes.
3. Relatively preserved lateral compartment.`,
    impression:"1. Moderate medial compartment osteoarthritis, Kellgren-Lawrence Grade III.\n2. Mild patellofemoral degenerative changes.",
    image_urls:[], is_outside_study:false, notes:"",
  },
  {
    id:"IMG012", patient_id:"P004", patient_name:"Patricia Monroe",
    study_type:"MRI", body_part:"Knee", laterality:"Right",
    ordered_by:"Dr. Walker", order_date:dAgo(7), study_date:null,
    facility:"SimonMed", priority:"routine", status:"ordered",
    report_text:null, impression:null, image_urls:[], is_outside_study:false,
    notes:"Ordered to evaluate meniscal pathology prior to considering surgical intervention.",
  },
  {
    id:"IMG013", patient_id:"P005", patient_name:"David Okafor",
    study_type:"X-ray", body_part:"Hand", laterality:"Right",
    ordered_by:"Dr. Walker", order_date:dAgo(38), study_date:dAgo(38),
    facility:"In-house", priority:"STAT", status:"resulted",
    report_text:`CLINICAL INDICATION: Right thumb UCL injury, evaluate for Stener lesion and fracture.

TECHNIQUE: PA, lateral, and oblique radiographs of the right thumb with stress views.

FINDINGS: No acute fracture identified. There is no bony avulsion at the ulnar aspect of the MCP joint. Stress radiographs demonstrate 15 degrees of increased ulnar collateral laxity compared to the contralateral thumb, suggesting UCL injury. No significant osseous pathology.

IMPRESSION:
1. No acute fracture or bony avulsion.
2. Stress views demonstrate 15-degree UCL laxity, consistent with UCL sprain/tear. MRI recommended to characterize Stener lesion.`,
    impression:"1. No acute fracture or bony avulsion.\n2. Stress views demonstrate 15-degree UCL laxity, consistent with UCL injury. MRI recommended to characterize Stener lesion.",
    image_urls:[], is_outside_study:true, notes:"Outside study from urgent care — patient brought films.",
  },
  {
    id:"IMG014", patient_id:"P005", patient_name:"David Okafor",
    study_type:"MRI", body_part:"Hand", laterality:"Right",
    ordered_by:"Dr. Walker", order_date:dAgo(35), study_date:dAgo(33),
    facility:"Radiology Partners", priority:"routine", status:"resulted",
    report_text:`CLINICAL INDICATION: Right thumb UCL injury. Evaluate for complete UCL tear and Stener lesion.

TECHNIQUE: MRI right hand without contrast using dedicated surface coil, 3T.

FINDINGS: The ulnar collateral ligament of the first MCP joint demonstrates complete disruption at its distal insertion with retraction of the torn ligament proximal to the adductor pollicis aponeurosis, consistent with a Stener lesion. The proper UCL is completely torn; the accessory UCL demonstrates partial tearing. The MCP joint capsule is intact. The volar plate is intact. No intra-articular loose bodies. The remaining hand tendons and ligaments are unremarkable. No occult fractures on T1 sequences.

IMPRESSION:
1. Complete tear of the ulnar collateral ligament (proper and accessory) of the right first MCP joint.
2. Stener lesion confirmed — the torn UCL is retracted proximal to the adductor pollicis aponeurosis, precluding healing without surgical intervention.
3. Intact MCP joint capsule and volar plate.`,
    impression:"1. Complete UCL tear of the right first MCP joint.\n2. Stener lesion confirmed — surgical repair required.\n3. Intact MCP joint capsule and volar plate.",
    image_urls:[], is_outside_study:false, notes:"Surgical repair scheduled based on these findings.",
  },
  {
    id:"IMG015", patient_id:"P001", patient_name:"James Hartley",
    study_type:"X-ray", body_part:"Shoulder", laterality:"Right",
    ordered_by:"Dr. Walker", order_date:dAgo(2), study_date:dAgo(1),
    facility:"In-house", priority:"routine", status:"resulted",
    report_text:`CLINICAL INDICATION: Post-operative right shoulder, evaluate hardware.

TECHNIQUE: AP and Y-view radiographs of the right shoulder.

FINDINGS: Post-surgical changes are present. Anchors are appropriately positioned along the greater tuberosity. No hardware loosening or migration. No acute fracture. No significant post-operative effusion identified on plain radiograph.

IMPRESSION:
1. Post-operative right shoulder with anchors in appropriate position.
2. No hardware complications identified.`,
    impression:"1. Post-operative right shoulder with anchors in appropriate position.\n2. No hardware complications identified.",
    image_urls:[], is_outside_study:false, notes:"2-week post-op rotator cuff repair film.",
  },
];

// ─── MOCK CLINICAL NOTES (fallback when Supabase returns empty) ───────────────
const MOCK_CLINICAL_NOTES = {
  "P001": [
    { id:"CN-P001-1", note_type:"Office Visit",   visit_date:dAgo(65), chief_complaint:"Right shoulder pain and weakness with overhead activity", status:"signed" },
    { id:"CN-P001-2", note_type:"Follow-Up Visit",visit_date:dAgo(45), chief_complaint:"Right shoulder pain — imaging review and surgical discussion", status:"signed" },
    { id:"CN-P001-3", note_type:"Post-Op Visit",  visit_date:dAgo(14), chief_complaint:"Post-operative rotator cuff repair follow-up", status:"signed" },
    { id:"CN-P001-4", note_type:"Post-Op Visit",  visit_date:dAgo(2),  chief_complaint:"2-week post-op shoulder check", status:"draft" },
  ],
  "P002": [
    { id:"CN-P002-1", note_type:"Office Visit",   visit_date:dAgo(60), chief_complaint:"Left knee pain and swelling after fall", status:"signed" },
    { id:"CN-P002-2", note_type:"Follow-Up Visit",visit_date:dAgo(30), chief_complaint:"Knee pain follow-up — MRI review", status:"signed" },
    { id:"CN-P002-3", note_type:"Pre-Op Visit",   visit_date:dAgo(10), chief_complaint:"Pre-operative evaluation for knee surgery", status:"signed" },
  ],
  "P003": [
    { id:"CN-P003-1", note_type:"Office Visit",   visit_date:dAgo(25), chief_complaint:"Right ring finger triggering and pain", status:"signed" },
    { id:"CN-P003-2", note_type:"Post-Op Visit",  visit_date:dAgo(7),  chief_complaint:"Trigger finger release post-op visit", status:"signed" },
    { id:"CN-P003-3", note_type:"Follow-Up Visit",visit_date:dAgo(2),  chief_complaint:"New numbness in right fingers", status:"draft" },
  ],
  "P004": [
    { id:"CN-P004-1", note_type:"Office Visit",   visit_date:dAgo(120),chief_complaint:"Right thumb CMC joint pain and weakness", status:"signed" },
    { id:"CN-P004-2", note_type:"Follow-Up Visit",visit_date:dAgo(40), chief_complaint:"Thumb arthritis — imaging and surgical planning", status:"signed" },
    { id:"CN-P004-3", note_type:"Pre-Op Visit",   visit_date:dAgo(15), chief_complaint:"Pre-operative evaluation for CMC arthroplasty", status:"signed" },
  ],
  "P005": [
    { id:"CN-P005-1", note_type:"Office Visit",   visit_date:dAgo(50), chief_complaint:"Right hand numbness and tingling, carpal tunnel symptoms", status:"signed" },
    { id:"CN-P005-2", note_type:"Post-Op Visit",  visit_date:dAgo(15), chief_complaint:"Carpal tunnel release post-op follow-up", status:"signed" },
    { id:"CN-P005-3", note_type:"Follow-Up Visit",visit_date:dAgo(2),  chief_complaint:"UCL injury follow-up, surgical planning discussion", status:"signed" },
  ],
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const daysWaiting = (orderDate) => {
  if (!orderDate) return 0;
  return Math.floor((Date.now() - new Date(orderDate)) / 86400000);
};
const parseImpression = (text) => {
  if (!text) return null;
  const idx = text.toUpperCase().indexOf("IMPRESSION:");
  if (idx === -1) return null;
  return text.slice(idx + "IMPRESSION:".length).trim();
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// ─── STUDY TYPE ICON ──────────────────────────────────────────────────────────
function StudyTypeIcon({ type, size = 28 }) {
  const cfg = STUDY_CONFIG[type] || STUDY_CONFIG["X-ray"];
  return (
    <div style={{
      width: size, height: size, borderRadius: 6,
      background: cfg.bg, border: `1px solid ${cfg.color}30`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.5, color: cfg.color, flexShrink: 0,
    }}>
      {cfg.icon}
    </div>
  );
}

// ─── REPORT VIEWER ────────────────────────────────────────────────────────────
function ReportViewer({ study }) {
  const impression = parseImpression(study.report_text);
  const reportBody = study.report_text
    ? (impression ? study.report_text.slice(0, study.report_text.toUpperCase().indexOf("IMPRESSION:")) : study.report_text).trim()
    : null;

  if (!study.report_text) {
    return (
      <div style={{ padding: "32px 0", textAlign: "center", color: C.text3 }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>Awaiting Results</div>
        <div style={{ fontSize: 11, marginTop: 4 }}>
          {study.status === "ordered" ? "Study not yet performed" : "Report pending from radiology"}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Full report text */}
      <div style={{
        fontFamily: "var(--mono)", fontSize: 12, lineHeight: 1.8,
        color: C.text2, whiteSpace: "pre-wrap",
        background: C.navy3, padding: "14px 16px", borderRadius: 8,
        border: `1px solid ${C.border}`,
      }}>
        {reportBody}
      </div>
      {/* Impression callout */}
      {impression && (
        <div style={{
          borderLeft: `3px solid ${C.teal}`, paddingLeft: 14,
          background: C.tealLt, borderRadius: "0 8px 8px 0",
          padding: "12px 16px 12px 14px",
          border: `1px solid ${C.teal}30`,
        }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: C.teal, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
            IMPRESSION
          </div>
          <div style={{ fontFamily: "var(--mono)", fontSize: 12, lineHeight: 1.8, color: C.text1, whiteSpace: "pre-wrap" }}>
            {impression}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CAMERA MODAL ─────────────────────────────────────────────────────────────
function CameraModal({ studyId, onSave, onClose }) {
  const videoRef   = useRef(null);
  const [stream,   setStream]   = useState(null);
  const [preview,  setPreview]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch (e) {
      setError("Camera access denied or not available. " + e.message);
    }
  };

  const stopCamera = () => {
    if (stream) stream.getTracks().forEach(t => t.stop());
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const base64 = canvas.toDataURL("image/jpeg", 0.8);
    stopCamera();
    setPreview(base64);
  };

  const handleSave = async () => {
    if (!preview || !studyId) return;
    setSaving(true);
    // Fetch current image_urls, append new photo, update
    const { data: current } = await db.from("imaging_studies").select("image_urls").eq("id", studyId).single();
    const existing = (current?.image_urls) || [];
    await db.from("imaging_studies").update({ image_urls: [...existing, preview] }).eq("id", studyId);
    setSaving(false);
    onSave(preview);
    onClose();
  };

  return (
    <div className="cmd-overlay" onClick={onClose}>
      <div className="cmd-modal slide-in" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center" }}>
          <span style={{ fontWeight: 500, fontSize: 13 }}>📷 Camera Capture</span>
          <button className="btn btn-ghost btn-icon btn-sm ml-auto" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 20 }}>
          {error && (
            <div className="notification notification-warn"><span>⚠</span><span>{error}</span></div>
          )}
          {!preview && !error && (
            <>
              <video ref={videoRef} autoPlay playsInline style={{ width: "100%", borderRadius: 8, background: C.navy3, marginBottom: 12 }} />
              <button className="btn btn-primary w-full" onClick={capturePhoto}>Take Photo</button>
            </>
          )}
          {preview && (
            <>
              <img src={preview} alt="Captured" style={{ width: "100%", borderRadius: 8, marginBottom: 12 }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : "Confirm & Save"}
                </button>
                <button className="btn btn-secondary" onClick={() => { setPreview(null); startCamera(); }}>Retake</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── IMAGE GALLERY ────────────────────────────────────────────────────────────
function ImageGallery({ images, onCapture, studyId, onImageAdded }) {
  const [fullImg, setFullImg] = useState(null);
  const [showCam, setShowCam] = useState(false);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: C.text2 }}>Images ({images.length})</span>
        <button className="btn btn-ghost btn-sm ml-auto" onClick={() => setShowCam(true)}>📷 Camera</button>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {images.map((src, i) => (
          <div key={i} style={{ width: 72, height: 72, borderRadius: 6, overflow: "hidden", cursor: "pointer", border: `1px solid ${C.border2}` }}
            onClick={() => setFullImg(src)}>
            <img src={src} alt={`Study ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
        ))}
        {images.length === 0 && (
          <div style={{ fontSize: 12, color: C.text3 }}>No images uploaded. Use camera capture or upload.</div>
        )}
      </div>
      {fullImg && (
        <div className="cmd-overlay" onClick={() => setFullImg(null)}>
          <div style={{ maxWidth: "90vw", maxHeight: "90vh" }} onClick={e => e.stopPropagation()}>
            <img src={fullImg} alt="Full size" style={{ maxWidth: "100%", maxHeight: "90vh", borderRadius: 8 }} />
          </div>
        </div>
      )}
      {showCam && (
        <CameraModal studyId={studyId} onSave={(img) => onImageAdded && onImageAdded(img)} onClose={() => setShowCam(false)} />
      )}
    </div>
  );
}

// ─── ORDER PANEL ─────────────────────────────────────────────────────────────
function OrderPanel({ patientId, patientName, onSaved, onClose, fullWidth = false }) {
  const appCtx = useApp();
  const providerName = appCtx?.currentProvider
    ? `${appCtx.currentProvider.first_name} ${appCtx.currentProvider.last_name}`.trim()
    : 'Provider';
  const [form, setForm] = useState({
    study_type: "X-ray", body_part: "Shoulder", laterality: "Right",
    priority: "Routine", facility: "In-house", ordered_by: "Dr. Walker",
    order_date: TODAY, clinical_indication: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleOrder = async (isStat) => {
    setSaving(true);
    const row = {
      patient_id:  patientId,
      practice_id: appCtx?.practiceId,
      study_type:  form.study_type,
      body_part:   form.body_part,
      laterality:  form.laterality,
      priority:    isStat ? "STAT" : form.priority,
      facility:    form.facility,
      ordered_by:  form.ordered_by || providerName,
      order_date:  form.order_date,
      notes:       form.clinical_indication,
      status:      "ordered",
    };
    try {
      const { data, error } = await db.from("imaging_studies").insert([row]).select().single();
      if (error) throw error;
      logAudit('create', 'imaging', data.id, row.patient_id, { study: row.study_type, body_part: row.body_part });
      setSaved(data);
      if (onSaved) onSaved(data);
    } catch (err) {
      console.error('[imaging] order insert failed:', err.message);
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div className="slide-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="notification notification-info">
          <span>✓</span>
          <span>{saved.priority === "STAT" ? "🚨 STAT order placed" : "Order placed successfully"}</span>
        </div>
        <div style={{ padding: "14px 16px", background: C.navy3, borderRadius: 8 }}>
          {[["Patient", patientName || saved.patient_id], ["Study", saved.study_type], ["Body Part", `${saved.body_part} ${saved.laterality}`],
            ["Priority", saved.priority], ["Facility", saved.facility], ["Ordering Provider", saved.ordered_by]].map(([l, v]) => (
            <div key={l} style={{ display: "flex", gap: 12, padding: "4px 0", borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
              <span style={{ color: C.text3, width: 120 }}>{l}</span>
              <span style={{ color: saved.priority === "STAT" && l === "Priority" ? C.red : C.text1 }}>{v}</span>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setSaved(null)}>New Order</button>
          {onClose && <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>}
        </div>
      </div>
    );
  }

  const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {onClose && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 14, fontWeight: 500 }}>Order New Study</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>
      )}
      <div style={fullWidth ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 } : {}}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="field">
            <div className="field-label">Study Type</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {STUDY_TYPES.map(t => {
                const cfg = STUDY_CONFIG[t];
                return (
                  <button key={t} onClick={() => set("study_type", t)} style={{
                    padding: "4px 10px", borderRadius: 6, border: `1px solid ${form.study_type === t ? cfg.color : C.border2}`,
                    background: form.study_type === t ? cfg.bg : "transparent",
                    color: form.study_type === t ? cfg.color : C.text2, fontSize: 12, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 5,
                  }}>
                    {cfg.icon} {t}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={grid2}>
            <div className="field">
              <div className="field-label">Body Part</div>
              <select className="field-input field-select" value={form.body_part} onChange={e => set("body_part", e.target.value)}>
                {BODY_PARTS.map(b => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div className="field">
              <div className="field-label">Laterality</div>
              <select className="field-input field-select" value={form.laterality} onChange={e => set("laterality", e.target.value)}>
                {LATERALITIES.map(l => <option key={l}>{l}</option>)}
              </select>
            </div>
          </div>

          <div style={grid2}>
            <div className="field">
              <div className="field-label">Priority</div>
              <div style={{ display: "flex", gap: 6 }}>
                {PRIORITIES.map(p => (
                  <button key={p} onClick={() => set("priority", p)} style={{
                    flex: 1, padding: "6px", borderRadius: 6, cursor: "pointer",
                    border: `1px solid ${form.priority === p ? (p === "STAT" ? C.red : C.teal) : C.border2}`,
                    background: form.priority === p ? (p === "STAT" ? C.redLt : C.tealLt) : "transparent",
                    color: form.priority === p ? (p === "STAT" ? C.red : C.teal) : C.text2, fontSize: 12, fontWeight: 500,
                  }}>{p}</button>
                ))}
              </div>
            </div>
            <div className="field">
              <div className="field-label">Order Date</div>
              <input type="date" className="field-input" value={form.order_date} onChange={e => set("order_date", e.target.value)} />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={grid2}>
            <div className="field">
              <div className="field-label">Facility</div>
              <select className="field-input field-select" value={form.facility} onChange={e => set("facility", e.target.value)}>
                {FACILITIES.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div className="field">
              <div className="field-label">Ordering Provider</div>
              <select className="field-input field-select" value={form.ordered_by} onChange={e => set("ordered_by", e.target.value)}>
                {PROVIDERS.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="field-label">Clinical Indication</div>
            <textarea className="field-input" rows={4} value={form.clinical_indication}
              onChange={e => set("clinical_indication", e.target.value)}
              placeholder="Describe clinical indication, relevant history, and specific question to be answered…"
              style={{ resize: "vertical" }} />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button className="btn btn-primary" disabled={saving} onClick={() => handleOrder(false)}>
              {saving ? "Placing…" : "Place Order"}
            </button>
            <button className="btn btn-secondary" style={{ borderColor: C.red, color: C.red }} disabled={saving} onClick={() => handleOrder(true)}>
              🚨 Place STAT Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── UPLOAD OUTSIDE STUDY PANEL ───────────────────────────────────────────────
function UploadOutsideStudyPanel({ patientId, patientName, onSaved, onClose }) {
  const appCtx = useApp();
  const providerName = appCtx?.currentProvider
    ? `${appCtx.currentProvider.first_name} ${appCtx.currentProvider.last_name}`.trim()
    : 'Provider';
  const [form, setForm] = useState({
    study_type: "X-ray",
    body_part: "",
    study_date: TODAY,
    facility: "",
    clinical_indication: "",
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setSaving(true);
    let report_url = null;
    if (file) {
      const uploadFile = file.type.startsWith("image/") ? (await compressImage(file) || file) : file;
      // Practice-scoped key (storage-sign enforces the leading practice_id segment).
      // report_url now stores the BARE KEY (private bucket); readers must resolve a
      // signed URL via storage.getSignedUrl("imaging", report_url). NO public URL.
      const path = `${appCtx?.practiceId}/outside/${patientId}/${Date.now()}_${file.name.replace(/\.[^.]+$/, '')}.${uploadFile.type === 'image/jpeg' ? 'jpg' : file.name.split('.').pop()}`;
      try {
        await storage.uploadFile("imaging", path, uploadFile, uploadFile.type || "application/octet-stream");
        report_url = path;
      } catch (e) {
        console.error('[imaging] upload failed:', e.message);
      }
    }
    const row = {
      patient_id:          patientId,
      practice_id:         appCtx?.practiceId,
      patient_name:        patientName,
      study_type:          form.study_type,
      body_part:           form.body_part,
      study_date:          form.study_date,
      order_date:          TODAY,
      facility:            form.facility,
      clinical_indication: form.clinical_indication,
      is_outside_study:    true,
      status:              "resulted",
      report_url,
      laterality:          "N/A",
      priority:            "routine",
      ordered_by:          providerName,
    };
    try {
      const { data, error } = await db.from("imaging_studies").insert([row]).select().single();
      if (error) throw error;
      logAudit('upload', 'imaging', data.id, row.patient_id, { study: row.study_type, outside_study: true });
      onSaved(data);
    } catch (err) {
      console.error('[imaging] upload insert failed:', err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15, color: C.text1 }}>Upload Outside Study</div>
          <div style={{ fontSize: 12, color: C.text3 }}>{patientName}</div>
        </div>
        <button className="btn btn-ghost btn-sm btn-icon ml-auto" onClick={onClose}>✕</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div className="field">
          <div className="field-label">Study Type</div>
          <select className="field-input field-select" value={form.study_type} onChange={e => set("study_type", e.target.value)}>
            {STUDY_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="field">
          <div className="field-label">Body Part</div>
          <input className="field-input" value={form.body_part} onChange={e => set("body_part", e.target.value)} placeholder="e.g. Shoulder, Knee…" list="body-parts-list" />
          <datalist id="body-parts-list">{BODY_PARTS.map(b => <option key={b} value={b} />)}</datalist>
        </div>
        <div className="field">
          <div className="field-label">Date of Study</div>
          <input type="date" className="field-input" value={form.study_date} onChange={e => set("study_date", e.target.value)} />
        </div>
        <div className="field">
          <div className="field-label">Facility Name</div>
          <input className="field-input" value={form.facility} onChange={e => set("facility", e.target.value)} placeholder="e.g. Outside Radiology Group" />
        </div>
      </div>

      <div className="field" style={{ marginTop: 12 }}>
        <div className="field-label">Clinical Notes</div>
        <textarea className="field-input" rows={3} value={form.clinical_indication} onChange={e => set("clinical_indication", e.target.value)} placeholder="Reason for outside study, relevant history…" style={{ resize: "vertical" }} />
      </div>

      <div className="field" style={{ marginTop: 12 }}>
        <div className="field-label">Attach File (PDF, JPG, PNG)</div>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files[0])}
          style={{ color: C.text2, fontSize: 12 }} />
        {file && <div style={{ fontSize: 11, color: C.teal, marginTop: 4 }}>Selected: {file.name}</div>}
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
        <button className="btn btn-primary" disabled={saving || !form.body_part} onClick={handleSubmit}>
          {saving ? "Uploading…" : "Upload Study"}
        </button>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

// ─── STUDY LIST ITEM ──────────────────────────────────────────────────────────
function StudyListItem({ study, active, onClick }) {
  const cfg   = STUDY_CONFIG[study.study_type] || STUDY_CONFIG["X-ray"];
  const days  = study.status === "ordered" || study.status === "scheduled" ? daysWaiting(study.order_date) : null;
  const sMap  = { ordered: "badge-gray", scheduled: "badge-amber", completed: "badge-teal", resulted: "badge-green" };
  return (
    <div onClick={onClick} style={{
      display: "flex", gap: 10, padding: "12px 14px", cursor: "pointer",
      background: active ? C.navy4 : "transparent",
      borderLeft: active ? `2px solid ${C.teal}` : "2px solid transparent",
      borderBottom: `1px solid ${C.border}`,
    }}>
      <StudyTypeIcon type={study.study_type} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <span style={{ fontWeight: 500, fontSize: 13, color: C.text1 }}>
            {study.body_part} {study.laterality !== "N/A" ? study.laterality : ""}
          </span>
          {study.priority === "STAT" && (
            <span style={{ fontSize: 10, fontWeight: 700, color: C.red, background: C.redLt, padding: "1px 6px", borderRadius: 4 }}>STAT</span>
          )}
          {study.is_outside_study && (
            <span style={{ fontSize: 10, color: C.amber, background: C.amberLt, padding: "1px 6px", borderRadius: 4 }}>Outside</span>
          )}
        </div>
        <div style={{ fontSize: 11, color: C.text3 }}>
          {study.study_type} · {study.facility} · {fmtDate(study.study_date || study.order_date)}
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 5 }}>
          <span className={`badge ${sMap[study.status] || "badge-gray"}`} style={{ fontSize: 10 }}>{study.status}</span>
          {days !== null && (
            <span style={{ fontSize: 10, color: days >= 7 ? C.red : days >= 3 ? C.amber : C.text3 }}>
              {days}d waiting
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── STUDY DETAIL PANEL ───────────────────────────────────────────────────────
function StudyDetail({ study, onUpdate }) {
  const [images, setImages]       = useState(study?.image_urls || []);
  const [linking, setLinking]     = useState(false);
  const [notes,   setNotes]       = useState([]);

  useEffect(() => {
    if (study) {
      setImages(study.image_urls || []);
      // Fetch notes for linking — filtered to this patient, signed or draft only
      db
        .from("clinical_notes")
        .select("id, note_type, visit_date, chief_complaint, status")
        .eq("patient_id", study.patient_id)
        .in("status", ["signed", "draft"])
        .is("archived_at", null)
        .order("visit_date", { ascending: false })
        .limit(10)
        .then(({ data }) => {
          // Fall back to mock notes when Supabase returns no rows (dev/demo mode)
          const rows = data && data.length > 0 ? data : (MOCK_CLINICAL_NOTES[study.patient_id] || []);
          setNotes(rows);
        });
    }
  }, [study?.id]);

  if (!study) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12, color: C.text3 }}>
        <div style={{ fontSize: 40 }}>◈</div>
        <div style={{ fontSize: 14 }}>Select a study to view details</div>
      </div>
    );
  }

  const cfg  = STUDY_CONFIG[study.study_type] || STUDY_CONFIG["X-ray"];
  const sMap = { ordered: "badge-gray", scheduled: "badge-amber", completed: "badge-teal", resulted: "badge-green" };

  const handleImageAdded = (img) => {
    const updated = [...images, img];
    setImages(updated);
    if (onUpdate) onUpdate({ ...study, image_urls: updated });
  };

  const linkNote = async (noteId) => {
    await db.from("imaging_studies").update({ linked_note_id: noteId }).eq("id", study.id);
    setLinking(false);
    if (onUpdate) onUpdate({ ...study, linked_note_id: noteId });
  };

  return (
    <div className="slide-in" style={{ display: "flex", flexDirection: "column", gap: 16, height: "100%", overflowY: "auto" }}>
      {/* Study header */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <StudyTypeIcon type={study.study_type} size={40} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: C.text1 }}>
            {study.study_type} — {study.body_part} {study.laterality !== "N/A" ? study.laterality : ""}
          </div>
          <div style={{ fontSize: 12, color: C.text3, marginTop: 3 }}>
            {study.facility} · Ordered by {study.ordered_by} · {fmtDate(study.order_date)}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <span className={`badge ${sMap[study.status] || "badge-gray"}`}>{study.status}</span>
            {study.priority === "STAT" && <span className="badge badge-red">STAT</span>}
            {study.is_outside_study && <span className="badge badge-amber">Outside Study</span>}
          </div>
        </div>
      </div>

      {/* Study metadata */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        {[
          ["Order Date", fmtDate(study.order_date)],
          ["Study Date", fmtDate(study.study_date)],
          ["Facility",   study.facility],
          ["Provider",   study.ordered_by],
          ["Study Type", study.study_type],
          ["Laterality", study.laterality],
        ].map(([l, v]) => (
          <div key={l} style={{ padding: "8px 10px", background: C.navy3, borderRadius: 6 }}>
            <div style={{ fontSize: 10, color: C.text3, textTransform: "uppercase", marginBottom: 3 }}>{l}</div>
            <div style={{ fontSize: 12, color: C.text1 }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Report */}
      <div className="card" style={{ background: C.navy3 }}>
        <div className="card-header"><span className="card-title">Radiology Report</span></div>
        <div className="card-body">
          <ReportViewer study={study} />
        </div>
      </div>

      {/* Image gallery */}
      <div className="card" style={{ background: C.navy3 }}>
        <div className="card-body">
          <ImageGallery images={images} studyId={study.id} onImageAdded={handleImageAdded} />
        </div>
      </div>

      {/* Link to note */}
      <div style={{ display: "flex", gap: 8 }}>
        {!linking ? (
          <button className="btn btn-secondary btn-sm" onClick={() => setLinking(true)}>
            🔗 Link to Note {study.linked_note_id ? "✓" : ""}
          </button>
        ) : (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flex: 1 }}>
            <select className="field-input field-select" style={{ flex: 1, padding: "5px 8px" }}
              defaultValue="" onChange={e => e.target.value && linkNote(e.target.value)}>
              <option value="">Select a note…</option>
              {notes.map(n => {
                const cc = n.chief_complaint ? ` — "${n.chief_complaint.length > 45 ? n.chief_complaint.slice(0, 45) + "…" : n.chief_complaint}"` : "";
                return (
                  <option key={n.id} value={n.id}>
                    {n.note_type} — {fmtDate(n.visit_date || n.dos)}{cc}
                  </option>
                );
              })}
              {notes.length === 0 && <option disabled>No notes found for this patient</option>}
            </select>
            <button className="btn btn-ghost btn-sm" onClick={() => setLinking(false)}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MARK AS RESULTED MODAL ───────────────────────────────────────────────────
function MarkResultedModal({ study, onSaved, onClose }) {
  const [report, setReport] = useState("");
  const [saving, setSaving] = useState(false);
  const impression = parseImpression(report);

  const handleSave = async () => {
    setSaving(true);
    await db.from("imaging_studies").update({
      status: "resulted", report_text: report, impression,
      study_date: study.study_date || TODAY,
    }).eq("id", study.id);
    setSaving(false);
    onSaved({ ...study, status: "resulted", report_text: report, impression });
    onClose();
  };

  return (
    <div className="cmd-overlay" onClick={onClose}>
      <div className="cmd-modal slide-in" style={{ width: 600, maxHeight: "85vh", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>Mark as Resulted — {study.study_type} {study.body_part} {study.laterality}</div>
            <div style={{ fontSize: 11, color: C.text3 }}>{study.patient_name} · {study.facility}</div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm ml-auto" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 20, flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="field" style={{ flex: 1 }}>
            <div className="field-label">Paste Radiology Report</div>
            <textarea className="field-input" rows={12} value={report} onChange={e => setReport(e.target.value)}
              placeholder="Paste full radiology report text here. Include IMPRESSION: section for automatic parsing."
              style={{ resize: "vertical", fontFamily: "var(--mono)", fontSize: 12, lineHeight: 1.7 }} />
          </div>
          {impression && (
            <div style={{ borderLeft: `3px solid ${C.teal}`, padding: "10px 14px", background: C.tealLt, borderRadius: "0 6px 6px 0" }}>
              <div style={{ fontSize: 10, color: C.teal, fontWeight: 600, textTransform: "uppercase", marginBottom: 6 }}>Impression (auto-parsed)</div>
              <div style={{ fontSize: 12, fontFamily: "var(--mono)", color: C.text1, whiteSpace: "pre-wrap" }}>{impression}</div>
            </div>
          )}
        </div>
        <div style={{ padding: "12px 20px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving || !report.trim()} onClick={handleSave}>
            {saving ? "Saving…" : "Save & Mark as Resulted"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DICOM UPLOAD MODAL ───────────────────────────────────────────────────────
const MODALITY_OPTIONS = ["XR","CT","MRI","US","MG","NM","PT","FL","CR","DX"];
const DICOM_BODY_PARTS = ["Hand","Wrist","Forearm","Elbow","Humerus","Shoulder","Clavicle",
  "Cervical Spine","Thoracic Spine","Lumbar Spine","Hip","Pelvis","Femur","Knee",
  "Tibia","Ankle","Foot","Chest","Abdomen","Whole Body","Other"];

function DICOMUploadModal({ patient, practiceId, onSaved, onClose }) {
  const [files,       setFiles]       = useState([]);
  const [isDragging,  setIsDragging]  = useState(false);
  const [form,        setForm]        = useState({
    modality: "", body_part: "", description: "", study_date: "",
    ordering_provider: "", reading_radiologist: "", report_text: "",
    study_uid: "",
  });
  const [uploading,   setUploading]   = useState(false);
  const [progress,    setProgress]    = useState(0);
  const [error,       setError]       = useState("");
  const dropRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-parse DICOM tags from the first file
  const parseDicomTags = async (file) => {
    try {
      const { default: dicomParser } = await import("dicom-parser");
      const arrayBuffer = await file.arrayBuffer();
      const byteArray = new Uint8Array(arrayBuffer);
      let ds;
      try {
        ds = dicomParser.parseDicom(byteArray, { untilTag: "x00200013" });
      } catch {
        return; // not a valid DICOM file — skip auto-parse
      }
      const tag = (t) => { try { return ds.string(t) || ""; } catch { return ""; } };

      const modality   = tag("x00080060");
      const bodyPart   = tag("x00180015");
      const desc       = tag("x00081030") || tag("x00080103E");
      const studyDate  = tag("x00080020");
      const studyUID   = tag("x0020000D");

      setForm(f => ({
        ...f,
        modality:    modality || f.modality,
        body_part:   bodyPart  || f.body_part,
        description: desc      || f.description,
        study_uid:   studyUID  || f.study_uid,
        study_date:  studyDate ? `${studyDate.slice(0,4)}-${studyDate.slice(4,6)}-${studyDate.slice(6,8)}` : f.study_date,
      }));
    } catch { /* not DICOM — ignore */ }
  };

  // Handle file selection (picker or drop)
  const handleFiles = async (incoming) => {
    const list = Array.from(incoming);
    // Flatten zip files: just collect — we won't unzip on the client, upload as-is
    setFiles(prev => {
      const names = new Set(prev.map(f => f.name));
      return [...prev, ...list.filter(f => !names.has(f.name))];
    });
    // Auto-parse from first .dcm or unknown-extension file
    const firstDicom = list.find(f =>
      f.name.endsWith(".dcm") || f.name.endsWith(".dicom") || !f.name.includes(".")
    );
    if (firstDicom) await parseDicomTags(firstDicom);
  };

  const onDrop = (e) => {
    e.preventDefault(); setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleUpload = async () => {
    if (!files.length || !form.body_part) return;
    setUploading(true); setError(""); setProgress(0);
    try {
      const fallbackPid = await getCurrentPracticeId();
      const pid = practiceId || fallbackPid || "unknown";
      const { email: uploadedByEmail } = await getCurrentUserInfo();
      const studyUID = form.study_uid || `EPMD.${Date.now()}`;
      const basePath = `${pid}/${patient.id}/${studyUID}`;

      // Insert dicom_studies row first
      const { data: studyRow, error: insertErr } = await db
        .from("dicom_studies")
        .insert([{
          practice_id:         pid,
          patient_id:          patient.id,
          study_uid:           studyUID,
          study_date:          form.study_date || null,
          modality:            form.modality   || null,
          body_part:           form.body_part,
          description:         form.description || null,
          image_count:         files.length,
          storage_path:        basePath,
          file_names:          [],
          source:              "upload",
          ordering_provider:   form.ordering_provider  || null,
          reading_radiologist: form.reading_radiologist || null,
          report_text:         form.report_text || null,
          report_status:       form.report_text ? "preliminary" : "pending",
          uploaded_by:         uploadedByEmail || null,
        }])
        .select()
        .single();

      if (insertErr) throw new Error(insertErr.message);

      // Upload each file to storage
      const uploadedNames = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_") || `file_${i}.dcm`;
        try {
          await storage.uploadFile("dicom-files", `${basePath}/${safeName}`, file, file.type || "application/octet-stream");
          uploadedNames.push(safeName);
        } catch (e) {
          console.warn(`Upload failed for ${safeName}:`, e.message);
        }
        setProgress(Math.round(((i + 1) / files.length) * 100));
      }

      // Update file_names list
      await db.from("dicom_studies")
        .update({ file_names: uploadedNames, image_count: uploadedNames.length })
        .eq("id", studyRow.id);

      // Also insert a linked imaging_studies row for chart timeline visibility
      const { data: imgRow } = await db.from("imaging_studies").insert([{
        patient_id:   patient.id,
        study_type:   mapModalityToStudyType(form.modality),
        body_part:    form.body_part,
        study_date:   form.study_date || null,
        order_date:   todayISO(),
        status:       "resulted",
        practice_id:  pid,
        description:  form.description || null,
        dicom_study_id: studyRow.id,
        report_text:  form.report_text || null,
        // report_status omitted — column added by migration; defaults to 'pending'
      }]).select().single();

      logAudit("upload", "dicom", studyRow.id, patient.id, { modality: form.modality, files: uploadedNames.length });
      setUploading(false);
      onSaved({ ...imgRow, _dicomStudyId: studyRow.id, _dicomStudy: { ...studyRow, file_names: uploadedNames } });
    } catch (e) {
      setError(e.message || "Upload failed.");
      setUploading(false);
    }
  };

  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 400, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "32px 16px", overflowY: "auto" }}
      onClick={onClose}>
      <div style={{ width: "100%", maxWidth: 660, background: C.navy2, borderRadius: 12, border: `1px solid ${C.border2}`, display: "flex", flexDirection: "column" }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.text1 }}>🩻 Upload DICOM Study</div>
            <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>{patient.first} {patient.last}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.text2, fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Drop zone */}
          <div
            ref={dropRef}
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            style={{
              border: `2px dashed ${isDragging ? C.teal : C.border2}`,
              borderRadius: 8, padding: "24px 16px", textAlign: "center",
              background: isDragging ? C.tealLt : C.navy3,
              transition: "all 150ms", cursor: "pointer",
            }}
            onClick={() => document.getElementById("dicom-file-input")?.click()}
          >
            <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: C.text1, marginBottom: 4 }}>
              {files.length > 0 ? `${files.length} file${files.length !== 1 ? "s" : ""} selected` : "Drag & drop DICOM files here"}
            </div>
            <div style={{ fontSize: 11, color: C.text3 }}>
              .dcm · .dicom · files without extension · .zip accepted
            </div>
            <input
              id="dicom-file-input"
              type="file"
              multiple
              accept=".dcm,.dicom,.zip,application/dicom,application/zip"
              style={{ display: "none" }}
              onChange={e => handleFiles(e.target.files)}
            />
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div style={{ maxHeight: 100, overflowY: "auto", background: C.navy3, borderRadius: 6, border: `1px solid ${C.border}`, padding: "6px 10px" }}>
              {files.map((f, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11, color: C.text2, padding: "2px 0" }}>
                  <span>📄 {f.name}</span>
                  <span style={{ color: C.text3 }}>{(f.size / 1024).toFixed(0)} KB</span>
                </div>
              ))}
            </div>
          )}

          {/* Form fields */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="field">
              <div className="field-label">Modality <span style={{ color: C.text3 }}>(auto-parsed)</span></div>
              <select className="field-input field-select" value={form.modality} onChange={e => set("modality", e.target.value)}>
                <option value="">— select —</option>
                {MODALITY_OPTIONS.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="field">
              <div className="field-label">Body Part *</div>
              <input className="field-input" value={form.body_part} onChange={e => set("body_part", e.target.value)}
                placeholder="e.g. Shoulder, Knee…" list="dicom-body-parts" />
              <datalist id="dicom-body-parts">{DICOM_BODY_PARTS.map(b => <option key={b} value={b} />)}</datalist>
            </div>
            <div className="field">
              <div className="field-label">Study Date <span style={{ color: C.text3 }}>(auto-parsed)</span></div>
              <input type="date" className="field-input" value={form.study_date} onChange={e => set("study_date", e.target.value)} />
            </div>
            <div className="field">
              <div className="field-label">Description <span style={{ color: C.text3 }}>(auto-parsed)</span></div>
              <input className="field-input" value={form.description} onChange={e => set("description", e.target.value)} placeholder="e.g. Right shoulder MRI" />
            </div>
            <div className="field">
              <div className="field-label">Ordering Provider</div>
              <input className="field-input" value={form.ordering_provider} onChange={e => set("ordering_provider", e.target.value)} placeholder="Dr. Walker" />
            </div>
            <div className="field">
              <div className="field-label">Reading Radiologist</div>
              <input className="field-input" value={form.reading_radiologist} onChange={e => set("reading_radiologist", e.target.value)} placeholder="Radiologist name" />
            </div>
          </div>

          <div className="field">
            <div className="field-label">Radiology Report (paste text)</div>
            <textarea className="field-input" rows={4} value={form.report_text} onChange={e => set("report_text", e.target.value)}
              placeholder="Paste the full radiology report text here (optional)…"
              style={{ resize: "vertical", fontFamily: "var(--mono,monospace)", fontSize: 11, lineHeight: 1.7 }} />
          </div>

          {/* Progress bar */}
          {uploading && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.text3, marginBottom: 4 }}>
                <span>Uploading files…</span><span>{progress}%</span>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: C.navy4, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${progress}%`, background: C.teal, transition: "width 200ms", borderRadius: 3 }} />
              </div>
            </div>
          )}

          {error && (
            <div style={{ padding: "8px 12px", borderRadius: 6, fontSize: 12, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#f87171" }}>
              {error}
            </div>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-primary"
              disabled={uploading || files.length === 0 || !form.body_part}
              onClick={handleUpload}>
              {uploading ? `Uploading… ${progress}%` : "Upload Study"}
            </button>
            <button className="btn btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Helper: map DICOM modality code to study_type label used in imaging_studies
function mapModalityToStudyType(modality) {
  const map = { XR:"X-ray", CR:"X-ray", DX:"X-ray", CT:"CT", MR:"MRI", MRI:"MRI",
    US:"Ultrasound", MG:"X-ray", NM:"Bone Scan", PT:"Bone Scan", FL:"Fluoroscopy" };
  return map[modality?.toUpperCase()] || "X-ray";
}

// ─── PHASE 2 STUB — Imaging center connections ────────────────────────────────
const IMAGING_CENTERS = [
  { id: "simonmed", name: "SimonMed Imaging",  logo: "🏥", desc: "Pull imaging reports and DICOM studies directly from SimonMed locations across Arizona." },
  { id: "banner",   name: "Banner Imaging",    logo: "🏥", desc: "Connect to Banner Health imaging to receive radiology results and DICOM files automatically." },
  { id: "smil",     name: "SMIL",             logo: "🏥", desc: "Southwest Medical Imaging — direct DICOM and report integration for Scottsdale/Phoenix area." },
];

function ImagingCenterConnections({ collapsed, onToggle }) {
  return (
    <div style={{ borderTop: `1px solid ${C.border}`, background: C.navy2 }}>
      <button
        onClick={onToggle}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "none", border: "none", cursor: "pointer", color: C.text2, fontSize: 12 }}
      >
        <span style={{ fontWeight: 500 }}>Connect Imaging Center</span>
        <span style={{ fontSize: 10 }}>{collapsed ? "▸" : "▾"}</span>
      </button>
      {!collapsed && (
        <div style={{ padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
          {IMAGING_CENTERS.map(ctr => (
            <div key={ctr.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: C.navy3, borderRadius: 8, border: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>{ctr.logo}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 12, color: C.text1, marginBottom: 2 }}>{ctr.name}</div>
                <div style={{ fontSize: 11, color: C.text3, lineHeight: 1.4, marginBottom: 6 }}>{ctr.desc}</div>
                <div style={{ position: "relative", display: "inline-block" }}>
                  <button
                    disabled
                    title="Coming soon — Phase 2"
                    style={{ padding: "4px 12px", borderRadius: 5, border: `1px solid ${C.border}`, background: C.navy4, color: C.text3, fontSize: 11, cursor: "not-allowed" }}
                  >
                    Connect
                  </button>
                  <span style={{ marginLeft: 8, fontSize: 10, color: C.amber, fontStyle: "italic" }}>Coming Soon</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// IMAGING TAB — patient chart (exported)
// ════════════════════════════════════════════════════════════════════════════════
export function ImagingTab({ patient }) {
  const [studies,             setStudies]             = useState([]);
  const [selected,            setSelected]            = useState(null);
  const [showOrder,           setShowOrder]           = useState(false);
  const [showUploadOutside,   setShowUploadOutside]   = useState(false);
  const [showDicomUpload,     setShowDicomUpload]     = useState(false);
  const [dicomViewer,         setDicomViewer]         = useState(null); // { studyId }
  const [practiceId,          setPracticeId]          = useState(null);
  const [centersCollapsed,    setCentersCollapsed]    = useState(true);

  useEffect(() => {
    getCurrentPracticeId().then((pid) => {
      setPracticeId(pid || null);
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    db.from("imaging_studies")
      .select("id, patient_id, study_type, body_part, laterality, study_date, order_date, status, impression, report_text, ordered_by, facility, priority, linked_note_id, practice_id, created_at, dicom_study_id, description")
      .eq("patient_id", patient.id)
      .order("order_date", { ascending: false }).limit(100)
      .then(({ data }) => {
        const result = data || [];
        setStudies(result);
        if (result.length > 0) setSelected(result[0]);
      });
  }, [patient.id]); // eslint-disable-line

  const handleOrderSaved = (row) => {
    const updated = [{ ...row, patient_name: `${patient.first} ${patient.last}` }, ...studies];
    setStudies(updated);
    setSelected(updated[0]);
    setShowOrder(false);
  };

  const handleUploadSaved = (row) => {
    const updated = [{ ...row, patient_name: `${patient.first} ${patient.last}` }, ...studies];
    setStudies(updated);
    setSelected(updated[0]);
    setShowUploadOutside(false);
  };

  const handleDicomSaved = (row) => {
    const updated = [{ ...row, patient_name: `${patient.first} ${patient.last}` }, ...studies];
    setStudies(updated);
    setSelected(updated[0]);
    setShowDicomUpload(false);
  };

  const handleUpdate = (updated) => {
    setStudies(s => s.map(st => st.id === updated.id ? updated : st));
    setSelected(updated);
  };

  return (
    <div style={{ display: "flex", gap: 0, height: "calc(100vh - var(--header-h) - 120px)", overflow: "hidden" }}>
      {/* Left panel — timeline */}
      <div style={{ width: "40%", flexShrink: 0, display: "flex", flexDirection: "column", borderRight: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ padding: "10px 12px", borderBottom: `1px solid ${C.border}`, display: "flex", gap: 5, flexWrap: "wrap" }}>
          <button className="btn btn-primary btn-sm" style={{ flex: 1, minWidth: 0 }}
            onClick={() => { setShowOrder(true); setShowUploadOutside(false); setShowDicomUpload(false); }}>
            + Order New Study
          </button>
          <button className="btn btn-secondary btn-sm"
            onClick={() => { setShowUploadOutside(true); setShowOrder(false); setShowDicomUpload(false); }}>
            Outside
          </button>
          <button className="btn btn-secondary btn-sm" title="Upload DICOM study (.dcm files)"
            onClick={() => { setShowDicomUpload(true); setShowOrder(false); setShowUploadOutside(false); }}>
            🩻 DICOM
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {studies.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: C.text3, fontSize: 12 }}>No imaging studies on file.</div>
          )}
          {studies.map(s => (
            <div key={s.id}>
              <StudyListItem study={s} active={selected?.id === s.id} onClick={() => setSelected(s)} />
              {/* View Images button — only for DICOM studies */}
              {s.dicom_study_id && (
                <div style={{ padding: "0 12px 8px", borderBottom: `1px solid ${C.border}` }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ fontSize: 10, padding: "3px 8px", color: C.teal, border: `1px solid ${C.teal}40`, width: "100%" }}
                    onClick={(e) => { e.stopPropagation(); setDicomViewer({ studyId: s.dicom_study_id }); }}
                  >
                    🩻 View DICOM Images
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Phase 2 — Imaging center connections (stub) */}
        <ImagingCenterConnections
          collapsed={centersCollapsed}
          onToggle={() => setCentersCollapsed(c => !c)}
        />
      </div>

      {/* Right panel — detail */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", minWidth: 0 }}>
        {showOrder ? (
          <OrderPanel
            patientId={patient.id}
            patientName={`${patient.first} ${patient.last}`}
            onSaved={handleOrderSaved}
            onClose={() => setShowOrder(false)}
          />
        ) : showUploadOutside ? (
          <UploadOutsideStudyPanel
            patientId={patient.id}
            patientName={`${patient.first} ${patient.last}`}
            onSaved={handleUploadSaved}
            onClose={() => setShowUploadOutside(false)}
          />
        ) : (
          <StudyDetail study={selected} onUpdate={handleUpdate} />
        )}
      </div>

      {/* DICOM Upload Modal */}
      {showDicomUpload && (
        <DICOMUploadModal
          patient={patient}
          practiceId={practiceId}
          onSaved={handleDicomSaved}
          onClose={() => setShowDicomUpload(false)}
        />
      )}

      {/* DICOM Viewer */}
      {dicomViewer && (
        <DICOMViewer
          studyId={dicomViewer.studyId}
          patientId={patient.id}
          onClose={() => setDicomViewer(null)}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// STANDALONE IMAGING PAGE
// ════════════════════════════════════════════════════════════════════════════════

// ─── ALL STUDIES TAB ─────────────────────────────────────────────────────────
function AllStudiesTab() {
  const [studies,  setStudies]  = useState(isDemoMode ? MOCK_IMAGING : []);
  const [filters,  setFilters]  = useState({ type: "", status: "", priority: "", dateFrom: "", dateTo: "", provider: "" });
  const [sortKey,  setSortKey]  = useState("order_date");
  const [sortDir,  setSortDir]  = useState(-1);
  const [detail,   setDetail]   = useState(null);
  const [page,     setPage]     = useState(1);

  useEffect(() => { setPage(1); }, [filters]);

  useEffect(() => {
    if (isDemoMode) return;
    db.from("imaging_studies").select("*").order("order_date", { ascending: false }).limit(200)
      .then(({ data }) => setStudies(data || []));
  }, []);

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const handleSort = (k) => { if (sortKey === k) setSortDir(d => d * -1); else { setSortKey(k); setSortDir(-1); } };
  const si = (k) => sortKey === k ? (sortDir === -1 ? " ↓" : " ↑") : "";

  const filtered = studies
    .filter(r => !filters.type     || r.study_type === filters.type)
    .filter(r => !filters.status   || r.status === filters.status)
    .filter(r => !filters.priority || r.priority === filters.priority)
    .filter(r => !filters.dateFrom || r.order_date >= filters.dateFrom)
    .filter(r => !filters.dateTo   || r.order_date <= filters.dateTo)
    .filter(r => !filters.provider || r.ordered_by === filters.provider)
    .sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      return sortDir * (av > bv ? 1 : av < bv ? -1 : 0);
    });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const sMap = { ordered: "badge-gray", scheduled: "badge-amber", completed: "badge-teal", resulted: "badge-green" };

  const TH = ({ k, label, right }) => (
    <th style={{ textAlign: right ? "right" : "left", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }} onClick={() => handleSort(k)}>
      {label}{si(k)}
    </th>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Filters */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <select className="field-input field-select" style={{ width: 130, padding: "5px 8px" }} value={filters.type} onChange={e => setF("type", e.target.value)}>
          <option value="">All Types</option>{STUDY_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
        <select className="field-input field-select" style={{ width: 130, padding: "5px 8px" }} value={filters.status} onChange={e => setF("status", e.target.value)}>
          <option value="">All Status</option>{["ordered","scheduled","completed","resulted"].map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="field-input field-select" style={{ width: 120, padding: "5px 8px" }} value={filters.priority} onChange={e => setF("priority", e.target.value)}>
          <option value="">All Priority</option>{["routine","STAT"].map(p => <option key={p}>{p}</option>)}
        </select>
        <input type="date" className="field-input" style={{ width: 140, padding: "5px 8px" }} value={filters.dateFrom} onChange={e => setF("dateFrom", e.target.value)} />
        <input type="date" className="field-input" style={{ width: 140, padding: "5px 8px" }} value={filters.dateTo}   onChange={e => setF("dateTo", e.target.value)} />
        <select className="field-input field-select" style={{ width: 140, padding: "5px 8px" }} value={filters.provider} onChange={e => setF("provider", e.target.value)}>
          <option value="">All Providers</option>{PROVIDERS.map(p => <option key={p}>{p}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm ml-auto" onClick={() => setFilters({ type:"",status:"",priority:"",dateFrom:"",dateTo:"",provider:"" })}>Clear</button>
      </div>

      <div className="card">
        <div className="table-wrap" style={{ margin: 0, maxHeight: 520, overflowY: "auto" }}>
          <table>
            <thead>
              <tr>
                <TH k="patient_name" label="Patient" />
                <TH k="study_type"   label="Type" />
                <TH k="body_part"    label="Body Part" />
                <TH k="laterality"   label="Side" />
                <TH k="order_date"   label="Ordered" />
                <TH k="study_date"   label="Study Date" />
                <TH k="facility"     label="Facility" />
                <TH k="priority"     label="Priority" />
                <TH k="status"       label="Status" />
                <TH k="ordered_by"   label="Provider" />
              </tr>
            </thead>
            <tbody>
              {paged.map(r => (
                <tr key={r.id} className="tr-clickable" onClick={() => setDetail(r)}>
                  <td style={{ fontWeight: 500, fontSize: 12 }}>{r.patient_name}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <StudyTypeIcon type={r.study_type} size={20} />
                      <span style={{ fontSize: 12 }}>{r.study_type}</span>
                    </div>
                  </td>
                  <td style={{ fontSize: 12 }}>{r.body_part}</td>
                  <td style={{ fontSize: 12, color: C.text2 }}>{r.laterality}</td>
                  <td style={{ fontFamily: "var(--mono)", fontSize: 11, color: C.text3 }}>{r.order_date}</td>
                  <td style={{ fontFamily: "var(--mono)", fontSize: 11, color: C.text3 }}>{r.study_date || "—"}</td>
                  <td style={{ fontSize: 11, color: C.text2 }}>{r.facility}</td>
                  <td>{r.priority === "STAT" ? <span className="badge badge-red">STAT</span> : <span className="badge badge-gray">Routine</span>}</td>
                  <td><span className={`badge ${sMap[r.status] || "badge-gray"}`}>{r.status}</span></td>
                  <td style={{ fontSize: 11, color: C.text2 }}>{r.ordered_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: "8px 16px", borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.text3 }}>
          {filtered.length} studies · Click any row to view report
        </div>
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} totalItems={filtered.length} pageSize={PAGE_SIZE} />
      </div>

      {detail && (
        <div className="cmd-overlay" onClick={() => setDetail(null)}>
          <div className="cmd-modal slide-in" style={{ width: 680, maxHeight: "88vh", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{detail.patient_name} — {detail.study_type} {detail.body_part}</div>
              <button className="btn btn-ghost btn-icon btn-sm ml-auto" onClick={() => setDetail(null)}>✕</button>
            </div>
            <div style={{ overflowY: "auto", padding: 20 }}>
              <StudyDetail study={detail} onUpdate={(upd) => { setStudies(s => s.map(x => x.id === upd.id ? upd : x)); setDetail(upd); }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PENDING RESULTS TAB ─────────────────────────────────────────────────────
function PendingResultsTab() {
  const [studies, setStudies] = useState(
    isDemoMode
      ? MOCK_IMAGING.filter(s => s.status === "ordered" || s.status === "scheduled")
          .sort((a, b) => {
            if (a.priority === "STAT" && b.priority !== "STAT") return -1;
            if (b.priority === "STAT" && a.priority !== "STAT") return  1;
            return a.order_date > b.order_date ? 1 : -1;
          })
      : []
  );
  const [marking, setMarking] = useState(null);

  useEffect(() => {
    if (isDemoMode) return;
    db.from("imaging_studies").select("*")
      .in("status", ["ordered", "scheduled"])
      .order("order_date", { ascending: true })
      .then(({ data }) => {
        const d = data || [];
        setStudies(d.sort((a, b) => {
          if (a.priority === "STAT" && b.priority !== "STAT") return -1;
          if (b.priority === "STAT" && a.priority !== "STAT") return  1;
          return a.order_date > b.order_date ? 1 : -1;
        }));
      });
  }, []);

  const handleSaved = (updated) => {
    setStudies(s => s.filter(x => x.id !== updated.id));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 13, color: C.text2 }}>{studies.length} studies pending results</span>
        <span className="badge badge-red" style={{ fontSize: 11 }}>{studies.filter(s => s.priority === "STAT").length} STAT</span>
      </div>

      <div className="card">
        {studies.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", color: C.text3 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>✓</div>
            <div>No pending studies — all results received.</div>
          </div>
        )}
        {studies.map((s, i) => {
          const days = daysWaiting(s.order_date);
          const isOld = days >= 7;
          const isMed = days >= 3;
          return (
            <div key={s.id} style={{
              display: "flex", alignItems: "center", gap: 14, padding: "14px 16px",
              borderBottom: i < studies.length - 1 ? `1px solid ${C.border}` : "none",
              background: s.priority === "STAT" ? "rgba(224,82,82,0.05)" : "transparent",
              borderLeft: s.priority === "STAT" ? `3px solid ${C.red}` : "3px solid transparent",
            }}>
              <StudyTypeIcon type={s.study_type} size={32} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 500, fontSize: 13 }}>{s.patient_name}</span>
                  {s.priority === "STAT" && <span className="badge badge-red" style={{ fontSize: 10 }}>STAT</span>}
                  {s.is_outside_study && <span className="badge badge-amber" style={{ fontSize: 10 }}>Outside</span>}
                </div>
                <div style={{ fontSize: 12, color: C.text2, marginTop: 2 }}>
                  {s.study_type} · {s.body_part} {s.laterality !== "N/A" ? s.laterality : ""} · {s.facility}
                </div>
                <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>
                  Ordered {fmtDate(s.order_date)} by {s.ordered_by}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{
                  fontSize: 14, fontWeight: 700, fontFamily: "var(--mono)",
                  color: isOld ? C.red : isMed ? C.amber : C.text2,
                }}>
                  {days}d
                </div>
                <div style={{ fontSize: 10, color: C.text3 }}>waiting</div>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setMarking(s)}>
                Mark as Resulted
              </button>
            </div>
          );
        })}
      </div>

      {marking && (
        <MarkResultedModal study={marking} onSaved={handleSaved} onClose={() => setMarking(null)} />
      )}
    </div>
  );
}

// ─── ORDER NEW TAB ────────────────────────────────────────────────────────────
function OrderNewTab({ patients = [] }) {
  const [selectedPt, setSelectedPt] = useState(null);
  const [patQuery,   setPatQuery]   = useState("");
  const [showDrop,   setShowDrop]   = useState(false);

  const patResults = showDrop && patQuery.length >= 1
    ? patients.filter(p => `${p.first} ${p.last}`.toLowerCase().includes(patQuery.toLowerCase())).slice(0, 6)
    : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 900 }}>
      {/* Patient search */}
      <div className="card">
        <div className="card-header"><span className="card-title">Patient</span></div>
        <div className="card-body" style={{ padding: "12px 16px" }}>
          <div style={{ position: "relative", maxWidth: 400 }}>
            <div className="search-wrap">
              <span className="search-icon">⌕</span>
              <input className="search-input" placeholder="Search patient…" value={patQuery}
                onChange={e => { setPatQuery(e.target.value); setShowDrop(true); if (!e.target.value) setSelectedPt(null); }}
                onFocus={() => setShowDrop(true)} />
            </div>
            {patResults.length > 0 && (
              <div className="search-results" style={{ zIndex: 20 }}>
                {patResults.map(p => (
                  <div key={p.id} className="search-result-item" onClick={() => {
                    setSelectedPt(p); setPatQuery(`${p.first} ${p.last}`); setShowDrop(false);
                  }}>
                    <span>{p.first} {p.last}</span>
                    <span style={{ marginLeft: "auto", fontFamily: "var(--mono)", fontSize: 11, color: C.text3 }}>{p.mrn}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {selectedPt && (
            <div style={{ display: "flex", gap: 10, marginTop: 10, padding: "8px 12px", background: C.navy3, borderRadius: 6, maxWidth: 400 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.teal, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: "#fff" }}>
                {selectedPt.first[0]}{selectedPt.last[0]}
              </div>
              <div>
                <div style={{ fontWeight: 500, fontSize: 13 }}>{selectedPt.first} {selectedPt.last}</div>
                <div style={{ fontSize: 11, color: C.text3 }}>{selectedPt.mrn} · {selectedPt.insurance_name}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedPt && (
        <div className="card slide-in">
          <div className="card-header"><span className="card-title">Order Details</span></div>
          <div className="card-body">
            <OrderPanel
              patientId={selectedPt.id}
              patientName={`${selectedPt.first} ${selectedPt.last}`}
              fullWidth={true}
            />
          </div>
        </div>
      )}

      {!selectedPt && (
        <div style={{ textAlign: "center", color: C.text3, padding: "40px 0", fontSize: 13 }}>
          Search for a patient above to place an imaging order.
        </div>
      )}
    </div>
  );
}

// ─── MAIN STANDALONE PAGE ─────────────────────────────────────────────────────
const PAGE_TABS = [
  { id: "all",     label: "All Studies" },
  { id: "pending", label: "Pending Results" },
  { id: "order",   label: "Order New" },
];

export default function ImagingPage({ patients = [] }) {
  const [tab, setTab] = useState("all");
  const [hc, setHc] = useState(() => isDemoMode ? {
    resulted: MOCK_IMAGING.filter(s => s.status === "resulted").length,
    pending:  MOCK_IMAGING.filter(s => s.status === "ordered" || s.status === "scheduled").length,
    stat:     MOCK_IMAGING.filter(s => s.priority === "STAT" && (s.status === "ordered" || s.status === "scheduled")).length,
  } : { resulted: 0, pending: 0, stat: 0 });

  useEffect(() => {
    if (isDemoMode) return;
    db.from("imaging_studies").select("status,priority")
      .then(({ data }) => {
        const d = data || [];
        setHc({
          resulted: d.filter(s => s.status === "resulted").length,
          pending:  d.filter(s => s.status === "ordered" || s.status === "scheduled").length,
          stat:     d.filter(s => s.priority === "STAT" && (s.status === "ordered" || s.status === "scheduled")).length,
        });
      });
  }, []);

  return (
    <div className="slide-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <div style={{ fontSize: 18, fontWeight: 600 }}>Imaging</div>
        <div style={{ fontSize: 13, color: C.text2 }}>
          {hc.resulted} resulted ·{" "}
          <span style={{ color: C.amber }}>{hc.pending} pending</span> ·{" "}
          <span style={{ color: C.red }}>{hc.stat} STAT</span>
        </div>
      </div>
      <div className="tabs" style={{ marginBottom: 0 }}>
        {PAGE_TABS.map(t => (
          <div key={t.id} className={`tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>{t.label}</div>
        ))}
      </div>
      {tab === "all"     && <AllStudiesTab />}
      {tab === "pending" && <PendingResultsTab />}
      {tab === "order"   && <OrderNewTab patients={patients} />}
    </div>
  );
}
